import { useMemo } from "react";
import { Pie, PieChart } from "recharts";

import { ChartLegend, ChartLegendContent } from "@/components/ui/ChartLegend";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  chartColorCssVarKey,
} from "@/components/ui/ChartTooltip";
import { cn } from "@/lib/utils";

/** One slice row: categorical `nameKey`, numeric `dataKey`, optional explicit `fill`. */
export type PieDonutDatum = Record<string, string | number | undefined> & {
  fill?: string;
};

export interface PieDonutChartProps {
  data: PieDonutDatum[];
  chartConfig: ChartConfig;
  dataKey: string;
  /** Field used for slice identity; values should match keys in `chartConfig` for theme colors. */
  nameKey: string;
  /** Optional title above the chart (chat typography: `text-xxs font-medium`). */
  title?: string | null;
  /** Fixed height for the chart block (px); omit for max-height–only sizing. */
  height?: number | null;
  /** Renders {@link ChartLegend} + {@link ChartLegendContent} below the donut. @default true */
  showLegend?: boolean;
  /** @default `"40%"` (chat donut) */
  innerRadius?: number | string;
  /** @default `"70%"` */
  outerRadius?: number | string;
  /** @default 2 */
  paddingAngle?: number;
  /** Passed to {@link ChartContainer}. */
  className?: string;
  /**
   * Pie tooltip: single pill row (swatch + name + count). @default true
   * @see ChartTooltipContent `compact`
   */
  tooltipCompact?: boolean;
  /** When `tooltipCompact` is false, forwarded to {@link ChartTooltipContent}. */
  tooltipHideLabel?: boolean;
  /**
   * Slice enter animation (Recharts). Default on; set false for reduced motion or tests.
   * @default true
   */
  isAnimationActive?: boolean;
  /**
   * Recharts v3: keyboard focus and ARIA on the chart SVG.
   * @default true
   */
  accessibilityLayer?: boolean;
}

const PieDonutChartEmpty = () => (
  <div className="flex w-full min-w-0 items-center justify-center rounded-md border border-dashed border-muted-foreground/25 py-4 text-xxs text-muted-foreground">
    No data to display
  </div>
);

/**
 * Donut/pie chart for chat and dashboards: same density tokens as HorizontalBar
 * (compact `text-xxs`, dashed empty state), default 40%/70% radii, padding, legend, shadcn tooltip.
 */
const PieDonutChart = ({
  data,
  chartConfig,
  dataKey,
  nameKey,
  title = null,
  height = null,
  showLegend = true,
  innerRadius = "40%",
  outerRadius = "70%",
  paddingAngle = 2,
  className,
  tooltipCompact = true,
  tooltipHideLabel = false,
  isAnimationActive = true,
  accessibilityLayer = true,
}: PieDonutChartProps) => {
  /**
   * Stable `data` reference when inputs are unchanged. Recharts `Pie` compares
   * `sectors` by reference in `getDerivedStateFromProps`; a new array every
   * render resets animation and can make the sweep invisible.
   */
  const dataWithFill = useMemo(
    () =>
      data.map((row) => {
        const raw = row[nameKey];
        const key = typeof raw === "string" ? raw : String(raw);
        const fill = row.fill ?? `var(--color-${chartColorCssVarKey(key)})`;
        return { ...row, fill };
      }),
    [data, nameKey],
  );

  const total = dataWithFill.reduce((sum, row) => {
    const v = (row as PieDonutDatum)[dataKey];
    const n = typeof v === "number" ? v : Number(v);
    return sum + (Number.isNaN(n) ? 0 : n);
  }, 0);

  if (total === 0 || dataWithFill.length === 0) {
    return (
      <div className="flex w-full min-w-0 flex-col gap-1.5">
        {title != null && title !== "" ? (
          <p className="m-0 text-xxs font-medium text-foreground">{title}</p>
        ) : null}
        <PieDonutChartEmpty />
      </div>
    );
  }

  return (
    <div
      className="flex w-full min-w-0 flex-col gap-1.5"
      style={height != null ? { minHeight: height, height } : undefined}
    >
      {title != null && title !== "" ? (
        <p className="m-0 text-xxs font-medium text-foreground">{title}</p>
      ) : null}
      <ChartContainer
        config={chartConfig}
        className={cn(
          "mx-auto aspect-square w-full justify-center",
          height == null ? "max-h-[240px]" : "max-h-[min(280px,100%)]",
          className,
        )}
      >
        <PieChart accessibilityLayer={accessibilityLayer}>
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                compact={tooltipCompact}
                hideLabel={tooltipHideLabel}
                nameKey={nameKey}
              />
            }
          />
          <Pie
            data={dataWithFill}
            dataKey={dataKey}
            nameKey={nameKey}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            paddingAngle={paddingAngle}
            stroke="transparent"
            isAnimationActive={isAnimationActive}
          />
          {showLegend ? (
            <ChartLegend
              verticalAlign="bottom"
              content={
                <ChartLegendContent
                  nameKey={nameKey}
                  className="flex-wrap gap-x-2 gap-y-0.5 pt-1.5 text-foreground dark:text-gray-300"
                />
              }
            />
          ) : null}
        </PieChart>
      </ChartContainer>
    </div>
  );
};

export default PieDonutChart;
